import React, { useState } from "react";
import { map } from "lodash";
import styled from "styled-components";
import { FontWeight, Spacing, content } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { Label, Radio, Text } from "@ui-kit/form";
import Loading from "@ui-kit/loading";
import Modal from "@ui-kit/modal";
import SafeHTML from "@ui-kit/safe-html";
import { tr } from "@radarly/i18n";
import { FeedbackReason, FeedbackReasons } from "@radarly/models/discovery";

export interface IProps {
  onCancel: () => void;
  onValidate: (reason: FeedbackReason, message?: string) => void;
  isLoading: boolean;
  locale: Locale;
}

const Loader = styled.div`
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Description = styled.span`
  color: ${(props) => content(props, "secondary")};
`;

const Section = styled.div`
  padding: ${Spacing.Large} 0;
  border-bottom: 1px solid ${(props) => content(props, "tertiary")};
  :last-child {
    border-bottom: none;
  }
`;

const ReasonsSection = styled(Section)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 12px;
`;

const ReasonRadio = styled(Radio)`
  font-weight: ${FontWeight.Normal};
`;

const MessageLabel = styled(Label)`
  font-weight: ${FontWeight.Bold};
`;

const MessageText = styled(Text)`
  margin-top: ${Spacing.Medium};
`;

const RadarlyInsightPagesDiscoveryModalsFeedback = ({
  onCancel,
  onValidate,
  isLoading,
  locale,
}: IProps) => {
  const [reason, setReason] = useState<FeedbackReason>();
  const [message, setMessage] = useState<string>("");

  return (
    <Modal
      title={tr(locale, `INSIGHT_PAGES::DISCOVERY::FEEDBACK::MODAL_TITLE`)}
      onCancel={onCancel}
      cancelLabel={tr(locale, "FORM::CANCEL")}
      validateLabel={tr(
        locale,
        `INSIGHT_PAGES::DISCOVERY::FEEDBACK::MODAL_VALIDATE`
      )}
      onValidate={() => onValidate(reason, message)}
      locale={locale}
      isValid={!!reason}
    >
      {isLoading ? (
        <Loader>
          <Loading />
        </Loader>
      ) : (
        <div>
          <SafeHTML
            as={Description}
            html={tr(
              locale,
              `INSIGHT_PAGES::DISCOVERY::FEEDBACK::MODAL_DESCRIPTION`
            )}
          />

          <ReasonsSection>
            {map(FeedbackReasons, (r) => (
              <ReasonRadio
                key={r}
                label={tr(
                  locale,
                  `INSIGHT_PAGES::DISCOVERY::FEEDBACK::REASONS::${r}`
                )}
                checked={r === reason}
                onClick={() => setReason(r)}
              />
            ))}
          </ReasonsSection>

          <Section>
            <MessageLabel
              children={tr(
                locale,
                `INSIGHT_PAGES::DISCOVERY::FEEDBACK::MESSAGE_LABEL`
              )}
            />
            <MessageText
              key="text"
              variant="primary"
              value={message}
              placeholder={tr(
                locale,
                `INSIGHT_PAGES::DISCOVERY::FEEDBACK::MESSAGE_PLACEHOLDER`
              )}
              onChange={(v) => setMessage(v)}
            />
          </Section>
        </div>
      )}
    </Modal>
  );
};

export default RadarlyInsightPagesDiscoveryModalsFeedback;
